/**
 * Desktop Project Overview — small read-only loaders over tables the project
 * workspace already uses (project_conversations, project_pictures). Each one
 * returns [] when the viewer lacks the same capability the full screen needs.
 */
import { supabase } from "../../supabase";
import { CAPABILITY, can } from "../permissions";
import { isOffline } from "../connectivity";
import { shouldSkipSupabase } from "../guestMode";
import { resolveOrganizationIdForQueries } from "../orgQueryScope";
import { loadProjectPictures } from "../projectPictures";

export async function loadRecentConversations(actor, projectId, limit = 3) {
  if (!projectId || shouldSkipSupabase(actor) || isOffline()) return [];
  if (!can(actor, CAPABILITY.PROJECT_TASK_VIEW)) return [];
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) return [];
  const { data, error } = await supabase
    .from("project_conversations")
    .select("conversation_id, title, started_at, ended_at, created_at")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  const rows = data || [];
  if (!rows.length) return [];
  let snippets = new Map();
  try {
    const { data: transcripts } = await supabase
      .from("project_conversation_transcripts")
      .select("conversation_id, full_text")
      .eq("organization_id", organizationId)
      .in("conversation_id", rows.map((row) => row.conversation_id));
    snippets = new Map((transcripts || []).map((t) => [t.conversation_id, String(t.full_text || "")]));
  } catch {
    snippets = new Map();
  }
  return rows.map((row) => ({
    id: row.conversation_id,
    title: row.title || "Project conversation",
    at: row.started_at || row.created_at,
    snippet: (snippets.get(row.conversation_id) || "").slice(0, 120),
  }));
}

export async function loadLatestPictures(actor, projectId, limit = 4) {
  if (!projectId || shouldSkipSupabase(actor) || isOffline()) return [];
  const rows = await loadProjectPictures(actor, projectId);
  return rows.filter((row) => row.previewUrl).slice(0, limit);
}
